/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { useSnackbar } from '@components/snackbar';
import { AlarmClockIcon, ReloadIcon } from '@sketchnote/icons';
import { CalendarReminder, CalendarRepeat } from '@sketchnote/shared-components';
import classNames from 'classnames';
import { formatISO, parseISO } from 'date-fns';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { useKanbanStore } from 'src/features/kanban';

import { useTasksStore } from '../store/useTasksStore';
import { KanbanColumnOption } from '../types/tasks';
import { DateRangePicker } from './DatePicker';

const { useMemo, useCallback, useState } = React;

export const EditItemAssignDate = () => {
  const taskId = useTasksStore((s) => s.currentTaskId);
  const { columns } = useKanbanStore();

  const selectKanbanColumnOptions: KanbanColumnOption[] = columns?.map((col) => {
    return { color: col.color, value: col.id, label: col.name };
  });

  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open] = useSnackbar();

  const onUpdateItemField = useTasksStore((s) => s.onUpdateItemField);

  const task = useTasksStore((s) => {
    if (taskId) {
      return s.tasks[taskId];
    }
  });

  const [taskRepeat, setTaskRepeat] = useState<CalendarRepeat>({});

  const [reminderOffset, setReminderOffset] = useState<CalendarReminder>('');

  const { isReadOnly } = useTaskAccess(taskId || '');

  const handleChange = (values) => {
    const dateValues = values?.map((date) => formatISO(date));
    setDates(dateValues);
  };

  const handleKeys = () => {
    const arr: any = [];
    if (dates[0] !== null && dates) {
      arr.push('assignDate', 'startDate', 'endDate');
    }
    if ([0, '-1', '-2'].includes(reminderOffset)) {
      arr.push('reminderOffset');
    }
    return arr;
  };

  const handleOnDone = React.useCallback(() => {
    if (task?.id) {
      setLoading(true);
      const startDate = dates.length > 1 ? dates[0] : undefined;
      const endDate = dates.length > 1 ? dates[1] : dates[0] || undefined;
      if (dates || reminderOffset) {
        onUpdateItemField({
          item: {
            assignDate: dates,
            id: task.id,
            reminderOffset: reminderOffset,
            startDate,
            endDate,
          },
          keys: handleKeys(),
        })
          .then((success) => {
            if (!success) {
              open({
                text: 'Something went wrong',
                duration: 2000,
                position: 'top-center',
                snackType: 'error',
              });
            }
          })
          .finally(() => setLoading(false));
      }

      if (!isEqual(taskRepeat, task?.recurring)) {
        onUpdateItemField({
          item: {
            id: task.id,
            recurring: taskRepeat,
          },
          keys: ['recurring'],
        })
          .then((success) => {
            if (!success) {
              open({
                text: 'Something went wrong',
                duration: 2000,
                position: 'top-center',
                snackType: 'error',
              });
            }
          })
          .finally(() => setLoading(false));
      }
    }
  }, [dates, onUpdateItemField, reminderOffset, task?.id, taskRepeat]);

  const onClearDateRange = () => {
    setReminderOffset(0);

    if (task?.id) {
      setLoading(true);
      onUpdateItemField({
        item: { assignDate: [], id: task.id, reminderOffset: 0 },
        keys: ['assignDate', 'reminderOffset', 'startDate', 'endDate'],
      })
        .then((success) => {
          if (!success) {
            open({
              text: 'Something went wrong',
              duration: 2000,
              position: 'top-center',
              snackType: 'error',
            });
          }
        })
        .finally(() => setLoading(false));
    }
  };

  const assignedDates = useMemo(() => {
    if (task?.assignDate?.length)
      return task.assignDate
        .map((date) => (date ? parseISO(date) : undefined))
        .filter((date) => !!date);
    return [];
  }, [task]);

  const onChangeReminderCallback = (reminderOffset: CalendarReminder) => {
    setReminderOffset(reminderOffset);
  };

  const onChangeRepeatCallback = (calendarRepeat: CalendarRepeat) => {
    setTaskRepeat(calendarRepeat);

    if (calendarRepeat?.type) {
      setReminderOffset(0);
    }
  };

  if (!task) return <></>;

  const value = useMemo(() => {
    const start = task.startDate && new Date(task.startDate);
    const end = task.endDate && new Date(task.endDate);
    return [start, end].filter(Boolean) as [Date, Date] | [Date];
  }, [task]);

  return (
    <span className={classNames(loading && 'opacity-50')}>
      <DateRangePicker
        value={value}
        onChange={handleChange}
        onDone={handleOnDone}
        onClearDateRange={onClearDateRange}
        inputClassName="date-picker bg-gray-100 p-12 w-full rounded-sm h-48 border border-gray-200 text-left"
        initialTab={assignedDates.length === 1 ? 'startEndDate' : 'date'}
        readOnly={isReadOnly || loading}
        readOnlyTooltipText={
          isReadOnly ? 'You do not have the required permission to edit tasks' : ''
        }
        showReminder={true}
        showRecurring={true}
        reminderOffset={task?.reminderOffset}
        onChangeReminder={onChangeReminderCallback}
        selectKanbanColumnOptions={selectKanbanColumnOptions}
        recurringData={task?.recurring}
        onChangeRepeat={onChangeRepeatCallback}
        endDateFirst
        popoverPlacement="bottom"
        placeholderText="-"
        CustomPopoverTrigger={({ showReminder, value, showRecurring }) => {
          return (
            <div className="flex items-center gap-6 w-full justify-center text-xs">
              <div>{value}</div>
              {showReminder && <AlarmClockIcon wrapperClass="text-blue-500 " />}
              {showRecurring && <ReloadIcon className="text-blue-500" />}
            </div>
          );
        }}
      />
    </span>
  );
};
